import { Combobox, Item, ItemActions, ItemHeader, ItemTitle } from '@ibsys/kit';
import { IconButton } from '@/shared/ui';
import { ExternalLinkIcon, XIcon } from 'lucide-react';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useCompetenciesForRule,
  useCompetencyTree,
  useLinkRules,
  coachingKeys,
  type CompetencyForRuleItem,
  type CompetencyResponse,
} from '@/entities/coaching';
import { useHasPermission } from '@/entities/profile';
import { useQueryClient } from '@tanstack/react-query';

type RuleCompetenciesProps = {
  ruleId?: number;
};

/** Рекурсивно собирает все листовые компетенции (уровень 2) */
const collectLeafCompetencies = (
  competencies: CompetencyResponse[],
): { id: number; name: string; parentName: string }[] => {
  const result: { id: number; name: string; parentName: string }[] = [];
  for (const root of competencies) {
    if (root.isDeleted) continue;
    for (const child of root.children) {
      if (child.isDeleted) continue;
      result.push({ id: child.id, name: child.name, parentName: root.name });
    }
  }
  return result;
};

export const RuleCompetencies: FC<RuleCompetenciesProps> = ({ ruleId }) => {
  const { t } = useTranslation('expertReport');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const allowAdmin = useHasPermission('ADMIN_EXPERT_REPORT');
  const { data: competenciesData } = useCompetenciesForRule(ruleId);
  const { data: treeData } = useCompetencyTree();
  const { mutate: linkRulesMutate } = useLinkRules();

  const competencies = competenciesData?.competencies ?? [];

  const linkedIds = useMemo(() => new Set(competencies.map((c) => c.id)), [competencies]);

  // Все листовые компетенции для dropdown
  const allLeaves = useMemo(() => {
    if (!treeData?.competencies) return [];
    return collectLeafCompetencies(treeData.competencies);
  }, [treeData?.competencies]);

  const comboboxOptions = useMemo(
    () =>
      allLeaves
        .filter((leaf) => !linkedIds.has(leaf.id))
        .map((leaf) => ({
          value: leaf.id,
          label: `${leaf.parentName} \u2192 ${leaf.name}`,
        })),
    [allLeaves, linkedIds],
  );

  /** Привязка правила к компетенции */
  const handleAdd = useCallback(
    (value: unknown) => {
      if (!ruleId) return;
      const competencyId = Number(value);

      // Получаем текущие ruleIds для этой компетенции из дерева
      const competency = findCompetencyInTree(treeData?.competencies ?? [], competencyId);
      if (!competency) return;

      const currentRuleIds = competency.rules.map((r) => r.id);
      linkRulesMutate(
        { id: competencyId, body: { ruleIds: [...currentRuleIds, ruleId] } },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: coachingKeys.forRule(ruleId) });
          },
        },
      );
    },
    [ruleId, treeData?.competencies, linkRulesMutate, queryClient],
  );

  /** Отвязка правила от компетенции */
  const handleRemove = useCallback(
    (competencyId: number) => {
      if (!ruleId) return;

      const competency = findCompetencyInTree(treeData?.competencies ?? [], competencyId);
      if (!competency) return;

      const currentRuleIds = competency.rules.map((r) => r.id).filter((id) => id !== ruleId);
      linkRulesMutate(
        { id: competencyId, body: { ruleIds: currentRuleIds } },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: coachingKeys.forRule(ruleId) });
          },
        },
      );
    },
    [ruleId, treeData?.competencies, linkRulesMutate, queryClient],
  );

  const handleNavigate = useCallback(
    (competencyId: number) => {
      navigate(`/competencies/${competencyId}`);
    },
    [navigate],
  );

  if (!ruleId) return null;

  if (competencies.length === 0) {
    return (
      <div className="space-y-2">
        <div className="text-sm font-medium text-muted-foreground">{t('competencies')}</div>
        <div className="text-sm text-muted-foreground">{t('noCompetencies')}</div>
        {allowAdmin && comboboxOptions.length > 0 && (
          <Combobox options={comboboxOptions} onChange={handleAdd} placeholder={t('addCompetency')} />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium text-muted-foreground">{t('competencies')}</div>

      <div className="flex flex-col gap-1">
        {competencies.map((comp: CompetencyForRuleItem) => (
          <Item key={comp.id} variant="outline" size="sm">
            <ItemHeader className="min-w-0">
              <ItemTitle className="min-w-0 text-sm">
                {/* Название переносится целиком, а не усекается, — показывать его подсказкой нечего */}
                <span className="min-w-0 break-words">{`${comp.parentName} → ${comp.name}`}</span>
              </ItemTitle>
              <ItemActions className="ml-4 shrink-0">
                <IconButton
                  variant="ghost"
                  size="icon"
                  onClick={() => handleNavigate(comp.id)}
                  label={t('goToCompetency')}
                  icon={<ExternalLinkIcon className="size-3.5" />}
                />
                {allowAdmin && (
                  <IconButton
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemove(comp.id)}
                    label={t('removeCompetency')}
                    icon={<XIcon className="size-3.5" />}
                  />
                )}
              </ItemActions>
            </ItemHeader>
          </Item>
        ))}
      </div>

      {allowAdmin && comboboxOptions.length > 0 && (
        <Combobox options={comboboxOptions} onChange={handleAdd} placeholder={t('addCompetency')} />
      )}
    </div>
  );
};

/** Рекурсивно находит компетенцию в дереве */
const findCompetencyInTree = (competencies: CompetencyResponse[], id: number): CompetencyResponse | undefined => {
  for (const c of competencies) {
    if (c.id === id) return c;
    const found = findCompetencyInTree(c.children, id);
    if (found) return found;
  }
  return undefined;
};
